import React, { useState, useEffect } from "react";
import { Moon, X, ExternalLink, ChevronRight } from "lucide-react";

const TUTORIALS = [
  { device: "iPhone / iPad", icon: "🍎", url: "https://support.apple.com/it-it/guide/iphone/iph3e2e1ce0/ios", steps: "Impostazioni → Schermo e luminosita → Scuro" },
  { device: "Android", icon: "🤖", url: "https://support.google.com/android/answer/9730472?hl=it", steps: "Impostazioni → Display → Tema scuro" },
  { device: "Chrome", icon: "🌐", url: "https://support.google.com/chrome/answer/9275525?hl=it", steps: "Impostazioni → Aspetto → Tema scuro" },
  { device: "WhatsApp", icon: "💬", url: "https://faq.whatsapp.com/1124475518307498/?helpref=hc_fnav", steps: "Impostazioni → Chat → Tema → Scuro" },
  { device: "Instagram", icon: "📷", url: "https://help.instagram.com/897760233943762", steps: "Profilo → Impostazioni → Tema → Scuro" },
  { device: "Gmail", icon: "📧", url: "https://support.google.com/mail/answer/112508?hl=it", steps: "Impostazioni → Tema → Scuro" },
  { device: "Windows", icon: "💻", url: "https://support.microsoft.com/it-it/windows/cambiare-i-colori-in-windows-d26ef4d6-819a-581c-1581-493cfcc005fe", steps: "Impostazioni → Personalizzazione → Colori → Scuro" },
  { device: "Mac", icon: "🖥️", url: "https://support.apple.com/it-it/guide/mac-help/mchl52e1c2d2/mac", steps: "Preferenze di Sistema → Aspetto → Scuro" },
];

const DarkModePopup = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem("sbp-darkmode-dismissed");
    if (dismissed) return;
    const timer = setTimeout(() => setVisible(true), 20000);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem("sbp-darkmode-dismissed", "1");
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4" data-testid="darkmode-popup">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={dismiss} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md bg-[#0c1220] border border-[#34A853]/30 rounded-2xl shadow-2xl overflow-hidden"
        style={{ animation: "dmPopIn 0.35s cubic-bezier(0.16,1,0.3,1)" }}
      >
        <style>{`
          @keyframes dmPopIn {
            from { opacity: 0; transform: scale(0.9) translateY(20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>

        {/* Header */}
        <div className="px-6 pt-5 pb-3 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#34A853]/15 rounded-xl flex items-center justify-center">
              <Moon className="w-5 h-5 text-[#34A853]" />
            </div>
            <div>
              <p className="text-white font-bold text-base">Hai gia attivato il Dark Mode?</p>
              <p className="text-gray-500 text-xs">Risparmi energia e proteggi i tuoi occhi</p>
            </div>
          </div>
          <button onClick={dismiss} className="text-gray-600 hover:text-white p-1 -mr-1 -mt-1" data-testid="darkmode-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          <p className="text-gray-400 text-sm leading-relaxed mb-4">
            Ogni pixel nero su schermo OLED e un <strong className="text-white">pixel spento</strong>.
            Meno energia, meno CO2. Noi lo facciamo gia — ora tocca a te.
          </p>

          {!expanded ? (
            <button
              onClick={() => setExpanded(true)}
              className="w-full bg-[#34A853] hover:bg-[#2d9249] text-white font-bold py-3 rounded-xl transition-all text-sm flex items-center justify-center gap-2"
              data-testid="darkmode-expand-btn"
            >
              <Moon className="w-4 h-4" /> Attivalo ovunque <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
              {TUTORIALS.map((t) => (
                <a
                  key={t.device}
                  href={t.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded-xl px-3 py-2.5 transition-colors group"
                  data-testid={`darkmode-tutorial-${t.device.toLowerCase().replace(/[^a-z]/g, '')}`}
                >
                  <span className="text-lg flex-shrink-0">{t.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-bold">{t.device}</p>
                    <p className="text-gray-500 text-[10px] truncate">{t.steps}</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-gray-600 group-hover:text-[#34A853] flex-shrink-0 transition-colors" />
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-1">
          <a
            href="/darkmode"
            className="text-[#34A853] text-xs font-bold hover:underline flex items-center gap-1"
            onClick={dismiss}
          >
            Scopri la nostra missione ecologica <ChevronRight className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default DarkModePopup;
